import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import translations, { Language } from './translations';
import settingsService from '../services/settings.service';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string>) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('erp_language');
    return (stored === 'cs' || stored === 'en') ? stored : 'en';
  });

  useEffect(() => {
    // Load default language from company settings
    const loadLang = async () => {
      try {
        const res = await settingsService.getCompanySettingsFlat();
        const langSetting = res.data.find((s: any) => s.key === 'default_language');
        if (langSetting?.value && (langSetting.value === 'en' || langSetting.value === 'cs')) {
          const stored = localStorage.getItem('erp_language');
          if (!stored) {
            setLanguageState(langSetting.value as Language);
            localStorage.setItem('erp_language', langSetting.value);
          }
        }
      } catch { /* keep default */ }
    };
    loadLang();

    // Listen for language change events
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && (detail === 'en' || detail === 'cs')) {
        setLanguageState(detail as Language);
        localStorage.setItem('erp_language', detail);
      }
    };
    window.addEventListener('languageChanged', handler);
    return () => window.removeEventListener('languageChanged', handler);
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('erp_language', lang);
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: lang }));
  }, []);

  const t = useCallback((key: string, params?: Record<string, string>): string => {
    let text = translations[language]?.[key] || translations['en']?.[key] || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(`{${k}}`, v);
      });
    }
    return text;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
